import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AnalyticEvents, FeatureFlag } from '@app/enums';
import { PayrollResources } from '@app/modules/payroll/payroll.resources';
import { CovidWageSubsidy } from '@app/modules/reporting/reports/covid-wage-subsidy';
import { AnalyticService, AuthService, PayrollSettingsService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { Settings } from '@app/services/payroll-settings.service';
import { Province } from '@app/types/province';
import { Benefit, JournalEntry, PayrollRegister, Remittance, Reports, YTD } from '@reporting/reports';
import { ENHANCED_REPORTS, EPT_REPORTS, RL1Preview, RqRemittance } from '@reporting/reports/enhanced-reports';
import { Subscription } from 'rxjs';
import { EnhancedPayrollReport } from '../../interfaces/report.interface';
import { BaseReport } from '../../interfaces/standard-reports.interface';
import { EPTReportJurisdiction } from '../../reports/enhanced-reports/ept-by-jurisdiction';

@Component({
    templateUrl: './payroll-overview.view.html',
    styleUrls: ['./payroll-overview.style.scss'],
})
export class PayrollOverviewView implements OnInit, OnDestroy {
    reports: BaseReport[] = [];
    showCovidWageSubsidyReport = false;
    payrollSettings?: Settings;
    vacationYTDBalancesReport = false;
    vacationBalancesReportTile: BaseReport = {
        name: 'reporting.payroll.vacationBalances.name',
        description: 'reporting.payroll.vacationBalances.description',
        routerLink: 'v2/vacation-pay-balances',
    };

    private payrollSettingsSubscription$?: Subscription;
    private hasQcPayrollFeature = false;

    constructor(
        private auth: AuthService,
        private analytics: AnalyticService,
        private http: HttpClient,
        private router: Router,
        private route: ActivatedRoute,
        private payrollSettingsService: PayrollSettingsService,
        private featureService: FeatureService
    ) {
        this.loadSettings();
    }

    async ngOnInit(): Promise<void> {
        await Promise.allSettled([this.loadSettings(), this.loadFlags()]);
        await this.setReports();
        await this.payrollSettingsService.load();
        this.payrollSettingsSubscription$ = this.payrollSettingsService.settings$.subscribe((settings) => {
            if (!Object.keys(settings).length) {
                return;
            }
            this.payrollSettings = settings;
            const displayVacationYtdBalancesReport = this.shouldDisplayVacationYtdBalancesReportTile();
            if (displayVacationYtdBalancesReport) {
                this.reports.push(this.vacationBalancesReportTile);
            }
        });
    }

    ngOnDestroy(): void {
        this.payrollSettingsSubscription$?.unsubscribe();
    }

    shouldDisplayVacationYtdBalancesReportTile(): boolean {
        return Boolean(this.vacationYTDBalancesReport);
    }

    showReport(report: BaseReport): void {
        this.trackClick(this.getAnalyticsEventNameForReport(report));

        this.router.navigate(this.getRelativeReportLink(report), { relativeTo: this.route });
    }

    private async loadFlags(): Promise<void> {
        const [vacationYTDBalancesReport, qcPayroll] = await this.featureService.hasMany(
            FeatureFlag.vacationYTDBalancesReport,
            FeatureFlag.qcPayroll
        );
        this.vacationYTDBalancesReport = vacationYTDBalancesReport;
        this.hasQcPayrollFeature = qcPayroll;
    }

    private async loadSettings(): Promise<void> {
        const {
            data: {
                attributes: { allowCovidWageSubsidy },
            },
        } = await this.http
            .get<{
                data: { attributes: { allowCovidWageSubsidy: boolean } };
            }>(PayrollResources.Setting.replace(':company', this.auth.company.id.toString()))
            .toPromise();

        this.showCovidWageSubsidyReport = allowCovidWageSubsidy;
    }

    /**
     * The Covid Wage Subsidy Report is only available to companies who
     * qualify for this subsidy. This is the only report in Humi that
     * not every company has access to. If we make more, there should
     * be a more generalized solution.
     */
    private async setReports(): Promise<void> {
        const reports = this.showCovidWageSubsidyReport
            ? Reports
            : Reports.filter((report) => report !== CovidWageSubsidy);

        const enhancedReports = ENHANCED_REPORTS.filter((enhancedReport) => {
            if (!this.hasQcPayrollFeature && (enhancedReport === RL1Preview || enhancedReport === RqRemittance)) {
                return false;
            }

            return true;
        });

        const EPTJurisdictions = Object.keys(await this.getEPTSummaryJurisdictions()) as Province[];
        const EPTReports = EPT_REPORTS.filter((report) => EPTJurisdictions.includes(report.jurisdiction));

        this.reports = [...reports.map((report) => new report()), ...enhancedReports, ...EPTReports];
    }

    private isEnhancedReport(report: BaseReport): report is EnhancedPayrollReport {
        return 'reportName' in report;
    }

    private getRelativeReportLink(report: BaseReport): string[] {
        const routerLink = typeof report.routerLink === 'string' ? [report.routerLink] : report.routerLink;
        if (this.isEnhancedReport(report)) {
            // Report is an instance of EnhancedPayrollReport
            return ['enhanced', ...routerLink];
        }

        return routerLink;
    }

    private trackClick(event?: AnalyticEvents): void {
        if (!event) {
            return;
        }

        this.analytics.trackEvent(event);
    }

    private getAnalyticsEventNameForReport(report: BaseReport): AnalyticEvents | undefined {
        if (report instanceof PayrollRegister) {
            return AnalyticEvents.ReportingPayrollPayrollRegisterClick;
        } else if (report instanceof YTD) {
            return AnalyticEvents.ReportingPayrollYTDReportClick;
        } else if (report instanceof Remittance) {
            return AnalyticEvents.ReportingPayrollRemittanceReportClick;
        } else if (report instanceof JournalEntry) {
            return AnalyticEvents.ReportingPayrollJournalEntryReportClick;
        } else if (report instanceof Benefit) {
            return AnalyticEvents.ReportingPayrollBenefitsReportClick;
        }
    }

    /**
     * Gets the EPT summary data mapped by jurisdiction (Province).
     * This is used to retrieve which province tiles we should display for the EPT reports.
     */
    private async getEPTSummaryJurisdictions(): Promise<EPTReportJurisdiction> {
        const endpoint = PayrollResources.EPTSummariesByJurisdiction.replace(
            ':company',
            this.auth.company!.id.toString()
        );

        return this.http.get<EPTReportJurisdiction>(endpoint).toPromise();
    }
}
